import React, { memo, useRef, useEffect, useMemo, Fragment } from 'react';
import moment from 'moment';
import Checkbox from '@material-ui/core/Checkbox';
import { FiberManualRecord as FiberManualRecordIcon } from '@material-ui/icons';
import { generatePDF } from '@abhisek507/html2multipagepdf';
import {
  Letter,
  Quality as ImageQuality,
  RegularPageSelector as pageSelector,
  PageElementSelector as pageElementSelector,
  PageWithMaxPossibleWidthSelector as pageWithMaxPossibleWidthSelector,
} from '@abhisek507/html2multipagepdf/constants';
import Input from '../../subComponents/input';
import Button from '../../subComponents/button';
import DateWrapper from '../../subComponents/date-wrapper';
import { SectionLabel, SignatureWrapper, QuestionAnswers, SOAPNotes } from '../Eversense';
import { ICD10CODES, OFFICE_INSTRUCTIONS } from '../../../constants/cmnTemplateForAxena.constant';
import { DateFormat, DEFAULT_FORMFIELD, MaxDate, DEFAULT_PDF_INFO, FAX_ProvidersAndCreatePDF, Email_ProvidersAndCreatePDF } from '../../../constants/cmnTemplateForEversense.constant';
import LevaLogo from '../../../assets/levaLogo.png';
import AxenaLogo from '../../../assets/axenaLogo.png';
import { preparePDF } from '../../../utils/referralForm';
import './styles.css';

export const CMNAxenaTemplateIdentifier = 'cmn-template-for-axena';

const Heading = memo(({
  value: {
    sentTo,
    sentDate,
  },
}) => (
  <div className='heading flex align-items-center justify-content-space-between'>
    <div className='flex min-width-180 width-180 heading-logo'>
      <div className='flex align-items-center justify-content-center'>
        <img src={LevaLogo} alt='leva_logo' className='height-40 width-180' />
      </div>
    </div>

    <div className='flex flex-column min-width-40 align-items-center'>
      <span className='bold letter-spacing-1'>MEDICAL ORDER</span>
      <span className='bold letter-spacing-1'>(CERTIFICATE OF MEDICAL NECESSITY)</span>
    </div>

    <div className='flex justify-content-flex-end min-width-180 width-180 heading-logo'>
      <div className='flex align-items-center justify-content-center'>
        <img src={AxenaLogo} alt='leva_logo' className='height-40 width-180' />
      </div>
    </div>
  </div>
));

const Footer = memo(({
  onSubmit: handleSubmit = () => {}
}) => (
  <div className='footer flex justify-content-flex-end m-t-4 d-none'>
    <Button
      variant='contained'
      color='primary'
      onClick={handleSubmit}
      label='Download Template'
    />
  </div>
));

const ScriptInfo = memo(() => (
  <div className='script-info'>
    <div className='flex align-items-center justify-content-center bold font-18'>
      This is a request for <span className='italic m-l-1 m-r-1'>Leva®</span> Pelvic Health System (HCPCS S9002)
    </div>
  </div>
));

const PatientInformation = memo(({
  value: {
    patientDob,
    patientFullName,
    patientAddress,
    patientState,
    patientZip,
    patientCity,
    patientPhone,
    patientInsuranceCompany,
    patientInsuranceNo,
    patientEmail,
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section patient-information m-t-8 p-1'>
    <SectionLabel display='PATIENT INFORMATION REQUIRED' />

    <div className='flex flex-column gap-4'>
      <div className='flex align-items-center'>
        <Input name='patientFullName' label='Patient Last, First Name:' className='flex flex-fill m-r-4' value={patientFullName} onChange={handleChange.bind(this, 'patientFullName')} />
        <DateWrapper name='patientDob' label='Date of Birth:' value={patientDob} onChange={handleChange.bind(this, 'patientDob')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='patientAddress' label='Patient Address:' className='flex flex-fill m-r-4' value={patientAddress} onChange={handleChange.bind(this, 'patientAddress')} />
        <Input name='patientState' label='State:' className='m-r-4' value={patientState} onChange={handleChange.bind(this, 'patientState')} />
        <Input name='patientZip' label='Zip:' value={patientZip} onChange={handleChange.bind(this, 'patientZip')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='patientCity' label='City:' className='flex flex-fill m-r-4' value={patientCity} onChange={handleChange.bind(this, 'patientCity')} />
        <Input name='patientEmail' label='Email:' className='min-width-260 m-r-4' value={patientEmail} onChange={handleChange.bind(this, 'patientEmail')} />
        <Input name='patientPhone' label='Phone:' value={patientPhone} onChange={handleChange.bind(this, 'patientPhone')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='patientInsuranceCompany' label='Insurance Company:' className='flex flex-fill m-r-4' value={patientInsuranceCompany} onChange={handleChange.bind(this, 'patientInsuranceCompany')} />
        <Input name='patientInsuranceNo' label='Insurance #:' value={patientInsuranceNo} onChange={handleChange.bind(this, 'patientInsuranceNo')} />
      </div>
    </div>
  </div>
));

const PrescriberInformation = memo(({
  value: {
    prescriberFullName,
    prescriberNpi,
    prescriberAddress,
    prescriberState,
    prescriberZip,
    prescriberCity,
    prescriberPhone,
    prescriberFax,
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section prescriber-information m-t-8 p-1'>
    <SectionLabel display='PRESCRIBER INFORMATION' />

    <div className='flex flex-column gap-4'>
      <div className='flex align-items-center'>
        <Input name='prescriberFullName' label='Prescriber Last, First Name:' className='flex flex-fill m-r-4'  value={prescriberFullName} onChange={handleChange.bind(this, 'prescriberFullName')} />
        <Input name='prescriberNpi' label='NPI #:' value={prescriberNpi} onChange={handleChange.bind(this, 'prescriberNpi')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='prescriberAddress' label='Prescriber Address:' className='flex flex-fill m-r-4' value={prescriberAddress} />
        <Input name='prescriberState' label='State:' className='m-r-4' value={prescriberState} />
        <Input type='text' name='prescriberZip' label='Zip:' value={prescriberZip} />
      </div>

      <div className='flex align-items-center'>
        <Input name='prescriberCity' label='City:' className='flex flex-fill m-r-4' value={prescriberCity} />
        <Input name='prescriberPhone' label='Phone:' className='m-r-4' value={prescriberPhone} />
        <Input name='prescriberFax' label='Fax:' value={prescriberFax} />
      </div>
    </div>
  </div>
));

const PrescriberOrder = memo(({
  value = {},
  onChange: handleChange = () => {}
}) => (
  <div className='section prescriber-order m-t-8'>
    <SectionLabel display="PRESCRIBER ORDER FOR LEVA PELVIC HEALTH SYSTEM (must be completed by prescriber or prescriber's staff)" />

    <div className='flex flex-column'>
      <div className='flex flex-fill font-14 p-l-1 p-t-1 p-b-1 p-r-1 bg-light-primary'>DIAGNOSIS AND ICD-10 CODES</div>
    </div>

    <div className='grid col-3 p-1'>
      {
        ICD10CODES.map(({ questionId, label, isModificationAllowed }) => (
          <div key={questionId} className={`flex mn-col align-items-center`}>
            <Checkbox
              title={`checkbox-${questionId}`}
              className='checkbox d-none'
              checked={value[`checkbox-${questionId}`]}
              disabled={!isModificationAllowed}
              onChange={(_, _value) => { handleChange(`checkbox-${questionId}`, { target: { value: _value } }); }}
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <FiberManualRecordIcon style={{ fontSize: 10 }} className='color-primary' />
            <div className='flex flex-fill align-items-center p-2'>{label}</div>
          </div>
        ))
      }
    </div>

    <div className='flex align-items-center p-l-1 p-r-1 p-b-1'>
      {/* <Checkbox
        title={`checkbox-OTHER`}
        className='checkbox d-none'
        checked={value['checkbox-OTHER']}
        onChange={(_, _value) => { handleChange('checkbox-OTHER', { target: { value: _value } }); }}
        inputProps={{ 'aria-label': 'primary checkbox' }}
      /> */}
      <div className='flex flex-column flex-fill'>
        <div className='flex align-items-center m-b-1'>
          <label className={`input-label m-r-1`}>Type the ICD-10 codes here:</label>
          <span className='required font-24 m-r-4'>*</span>
        </div>
        <textarea
          required
          name='otherICD10Code'
          className='flex flex-fill border-light-grey-1 min-height-100 height-full p-4 font-proxima-nova font-14 color-dark-grey border-box resize-none'
          wrap='soft'
          maxLength={500}
          value={value.otherICD10Code}
          onChange={handleChange.bind(this, 'otherICD10Code')}
        />
      </div>
    </div>

    <div className='flex flex-column border-top-primary'>
      <div className='flex flex-fill font-14 p-1 bg-light-primary'>
        <span className='italic m-r-1'>Leva®</span> Pelvic Health System - 1<sup className='m-r-1'>st</sup> - line Pelvic Floor Muscle Training (PFMT) treatment and monitoring for urinary or fecal incontinence.
      </div>

      <div className='flex flex-column gap-4 p-1'>
        <div className='flex flex-fill align-items-center'>
          <span className='input-label m-r-4'>Quantity:</span>
          <span className='font-14 m-r-4'>1 with PRN replacements for 12 months. If other, specify:</span>
          <Input
            name='prnReplacementQuantity'
            label=''
            className='flex flex-fill m-r-2'
            value={value.prnReplacementQuantity}
            onChange={handleChange.bind(this, 'prnReplacementQuantity')}
          />
        </div>

        <div className='flex flex-fill align-items-flex-start'>
          <span className='input-label m-r-4'>Directions for Use:</span>
          <div className='flex flex-column flex-fill'>
            <span className='height-30 font-14 m-r-4'>Use twice daily (am & pm), approximately 2.5 minutes each time following app training mode. Remove after each use.</span>
            <div className='flex align-items-center'>
              <span className='font-14 m-r-4'>If different directions for use apply, please indicate:</span>
              <Input
                name='directionsForUseApply'
                label=''
                className='flex flex-fill m-r-2'
                value={value.directionsForUseApply}
                onChange={handleChange.bind(this, 'directionsForUseApply')}
              />
            </div>
          </div>
        </div>

        <div className='flex flex-fill align-items-center justify-content-center align-center font-14'>
          I certify I am the Prescriber identified on this form and authorized by law to order the product requested herein. I also certify the prescribed treatment is medically necessary, reasonable and appropriate according to accepted standards within the medical community for the treatment of the patient's diagnosed condition.
        </div>

        <div className='flex align-items-center'>
          <SignatureWrapper
            name='hcpSignature'
            label='Electronically approved by:'
            value={value.hcpSignature}
            onChange={handleChange.bind(this, 'hcpSignature')}
          />
          <DateWrapper name='signatureDate' label='Date:' value={value.signatureDate} max={MaxDate} />
        </div>
      </div>
    </div>
  </div>
));

const OfficeInstructions = memo(({
  value = {},
  onChange: handleChange = () => {}
}) => (
  <div className='section office-instructions m-t-8'>
    <SectionLabel display="OFFICE INSTRUCTIONS. IN ADDITION TO THIS CMN, PLEASE ATTACH AND INCLUDE THE FOLLOWING DOCUMENTS:" />

    {/*
      We can use grid boxes to render this section better when there are multiple objects.
      HTML snippet for grid box: <div className='grid col-2 p-2'></div>
    */}
    <div className='flex align-items-center justify-content-center p-1'>
      {
        OFFICE_INSTRUCTIONS.map(({ questionId, label, isModificationAllowed }) => (
          <div key='insurance-card' className={`flex mn-col align-items-center`}>
            <Checkbox
              title={`checkbox-${questionId}`}
              className='checkbox'
              disabled={!isModificationAllowed}
              checked={value[`checkbox-${questionId}`]}
              onChange={(_, _value) => { handleChange(`checkbox-${questionId}`, { target: { value: _value } }); }}
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <div className='flex flex-fill align-items-center p-2'>{label}</div>
          </div>
        ))
      }
    </div>

    <div className='flex flex-fill align-items-center justify-content-center align-center font-16 bold height-30'>
      Please send the fully completed and signed <span className='italic m-l-1 m-r-1'>Leva®</span> prescription order form by
    </div>

    <div className='flex flex-fill align-items-center justify-content-center align-center font-16 bold m-b-1'>
      Fax: {value.storeFax} or Email .pdf file: {value.storeEmail}
    </div>
  </div>
));

const SectionA = ({
  value = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {},
}) => (
  <div className={`page border-primary ${pageSelector} ${className}`}>
    <Heading value={value} onChange={handleChange} />
    <div className={`content flex flex-column gap-4 ${pageElementSelector}`}>
      <ScriptInfo />
      <PatientInformation value={value} onChange={handleChange} />
      <PrescriberInformation value={value} onChange={handleChange} />
      <PrescriberOrder value={value} onChange={handleChange} />
      <OfficeInstructions  value={value} onChange={handleChange} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
);

const SectionB = memo(({
  value = {},
  consultInfo = {},
  className = '',
  heading: Heading = Fragment,
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => {
  const { qAndAs } = useMemo(() => {
    try {
      const qAndAs = consultInfo.ProductConsultDetails;
      return { qAndAs };
    } catch(e) {
      return { qAndAs: [] };
    }
  }, [consultInfo]);

  return (
    <QuestionAnswers
      value={value}
      qAndAs={qAndAs}
      className={className}
      heading={Heading}
      footer={Footer}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
});

export const SectionC = memo(({
  value = {},
  notes = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div className={`page border-primary flex flex-column max-width-1024 ${pageWithMaxPossibleWidthSelector} ${className} d-none`}>
    <Heading value={value} onChange={handleChange} />
    <div className='content flex flex-column flex-fill m-b-5'>
      <SOAPNotes notes={notes} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
));

export const CMNTemplateForAxena = ({
  id='',
  reference,
  className = '',
  value = {},
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div ref={reference} id={id} className={CMNAxenaTemplateIdentifier}>
    <SectionA
      value={value}
      className={className}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />

    <SectionC
      value={value}
      notes={notes}
      className={className}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />

    <SectionB
      value={value}
      consultInfo={consultInfo}
      className={className}
      heading={Heading}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  </div>
);

export const TemplateDownloadManager = ({
  id = '',
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  formField = { ...DEFAULT_FORMFIELD },

  getCmnPdfData = async () => {},
  setFormField = () => {}
}) => {
  const reference = useRef();

  const handleSubmit = (event) => {
    event.preventDefault();
    preparePDF(
      CMNAxenaTemplateIdentifier,
      [pageSelector, pageWithMaxPossibleWidthSelector],
      Letter,
      pageElementSelector,
      ImageQuality[100],
      generatePDF
    ).then(() => {
      // code snippet if required
    });
  };

  const handleChange = (name, event) => {
    setFormField({ ...formField, [name]: event.target.value });
  };

  const loadData = async () => {
    const response  = await getCmnPdfData({
      OrderId: productInfo.OrderId,
      PaitentId: patientInfo.UserId,
      template: 'Axena'
    });

    const pdfData = (() => {
      try {
        return response.data.Data || DEFAULT_PDF_INFO();
      } catch(e) {
        return DEFAULT_PDF_INFO();
      }
    })();

    let nextFormField = {
      ...DEFAULT_FORMFIELD,

      patientDob: moment(pdfData.patientInfo.dob).format(DateFormat),
      patientFullName: `${pdfData.patientInfo.lastName}, ${pdfData.patientInfo.firstName}`,
      patientAddress: pdfData.patientInfo.address,
      patientState: pdfData.patientInfo.state,
      patientZip: pdfData.patientInfo.zip,
      patientCity: pdfData.patientInfo.city,
      patientPhone: pdfData.patientInfo.phone,
      patientEmail: pdfData.patientInfo.email,

      prescriberPreName: pdfData.prescriberInfo.preName,
      prescriberFullName: `${pdfData.prescriberInfo.lastName}, ${pdfData.prescriberInfo.firstName}`,
      prescriberNpi: pdfData.prescriberInfo.npiNo,

      lastVisitDate: MaxDate,

      hcpSignature: `${pdfData.provider.signature.prifix} ${pdfData.provider.signature.firstName} ${pdfData.provider.signature.lastNmae}`,
      signatureDate: MaxDate,
    };

    const config = (() => {
      try {
        return new Map(productInfo.StoreFrontData.StorefrontApplicationSettings) || new Map([['', {}]]);
      } catch(e) {
        return new Map([['', {}]]);
      }
    })();

    if (config.has(FAX_ProvidersAndCreatePDF)) {
      nextFormField.storeFax = config.get(FAX_ProvidersAndCreatePDF).value;
    }

    if (config.has(Email_ProvidersAndCreatePDF)) {
      nextFormField.storeEmail = config.get(Email_ProvidersAndCreatePDF).value;
    }

    console.log('@logger - initializing the pdf template', {
      config,
      pdfData,
      pdfInfo: productInfo.pdfInfo,
    });

    setFormField(nextFormField);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <CMNTemplateForAxena
      id={id}
      reference={reference}
      className='m-b-4 p-1'
      value={formField}
      notes={notes}
      productInfo={productInfo}
      patientInfo={patientInfo}
      consultInfo={consultInfo}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
};

export default memo(TemplateDownloadManager);
